import { LoadingButton } from "@mui/lab"
import {
  Button,
  CardActions,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material"
import {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  addDays,
  DialogTransition,
  isValidURL,
  lowercaseFirstCharacter,
  stripHtml,
  testYtURL,
} from "../../services/utilities/utility"
import DialogTopBar from "../global/dialogTopBar"
import LoadingBackdrop from "../global/loadingBackdrop"
import ImagePicker from "../global/imagePicker"
import { BodySliceType, MediaSize } from "../../services/config/enum"
import { useNavigate } from "react-router-dom"
import { MainContext } from "../../controllers/main"
import { LanguagesContext } from "../../controllers/languages"
import { NewsContext } from "../../controllers/news"
import EmbedSlice from "../../models/bodySlices/embedSlice"
import ImageSlice from "../../models/bodySlices/imageSlice"
import QuoteSlice from "../../models/bodySlices/quoteSlice"
import TextSlice from "../../models/bodySlices/textSlice"
import BlockEditor from "../blockEditor/blockEditor"
import News from "../../models/news"
import { CloseRounded } from "@mui/icons-material"
import TeamsList from "../../views/team/teamsList"
import Team from "../../models/team"
import { TeamsContext } from "../../controllers/teams"
import constraints from "../../services/config/constraints"

const CreateNewsDialog = ({
  dialogOpen,
  setDialogOpen,
  handleDialogChange,
  topBarText,
  preSelectedTeam,
  currentTeamNewsList,
  setCurrentTeamNewsList,
}: {
  dialogOpen: boolean
  setDialogOpen: Dispatch<SetStateAction<boolean>>
  handleDialogChange: MouseEventHandler<HTMLButtonElement>
  topBarText: string
  preSelectedTeam?: { id: string; name: string }
  currentTeamNewsList?: News[]
  setCurrentTeamNewsList?: Dispatch<SetStateAction<News[]>>
}) => {
  const { setAnimation } = useContext(MainContext)
  const { languages, languagesForEpisodeTabs } = useContext(LanguagesContext)
  const { loading, setLoading, createNews, createNewsDocument } =
    useContext(NewsContext)
  const { resetTeamsFilters } = useContext(TeamsContext)
  const navigate = useNavigate()

  // prevent tab close
  const handleTabClose = useCallback((event: any) => {
    event.preventDefault()
    console.log("beforeunload event triggered")
    return (event.returnValue = "Are you sure you want to exit?")
  }, [])

  useEffect(() => {
    if (dialogOpen) {
      window.addEventListener("beforeunload", handleTabClose)
    } else {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [dialogOpen])

  // start date
  const [startsAt, setStartsAt] = useState<string>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    ).toISOString()
  )
  const [startsAtError, setStartsAtError] = useState<boolean>(false)

  // end date
  const [endsAt, setEndsAt] = useState<string>(
    addDays(
      new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate(),
        23,
        59,
        59
      ),
      1
    ).toISOString()
  )
  const [endsAtError, setEndsAtError] = useState<boolean>(false)

  // team
  const [team, setTeam] = useState<{ id: string; name: string }>(
    preSelectedTeam
      ? preSelectedTeam
      : {
          id: "team_default",
          name: "Global (App)",
        }
  )
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [changeTeamDialogOpen, setChangeTeamDialogOpen] =
    useState<boolean>(false)

  // default translation
  const [defaultTranslation, setDefaultTranslation] = useState<string>("")

  // title
  const [title, setTitle] = useState<string>("")

  // tagline
  const [tagline, setTagline] = useState<string | null>(null)

  // redirect url
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)

  // image
  const [image, setImage] = useState<string>("")

  // body
  const [body, setBody] = useState<
    (EmbedSlice | ImageSlice | QuoteSlice | TextSlice)[]
  >([])

  // check errors in body
  const [bodyError, setBodyError] = useState<boolean>(false)

  useEffect(() => {
    let errorInBody = false
    body.forEach((bodyItem) => {
      if (
        (bodyItem.sliceType === BodySliceType.BodyTextSlice &&
          !stripHtml(bodyItem.text).length) ||
        (bodyItem.sliceType === BodySliceType.BodyQuoteSlice &&
          !bodyItem.quoteText.length) ||
        (bodyItem.sliceType === BodySliceType.BodyImageSlice &&
          (!bodyItem.imgUrl.length || !isValidURL(bodyItem.imgUrl))) ||
        (bodyItem.sliceType === BodySliceType.BodyEmbedSlice &&
          (!bodyItem.embedUrl.length || !testYtURL(bodyItem.embedUrl)))
      ) {
        errorInBody = true
      }
    })
    if (errorInBody) {
      setBodyError(true)
    } else {
      setBodyError(false)
    }
  }, [body])

  // current slide and scrolling when changes
  const [currentSlide, setCurrentSlide] = useState<number>(0)

  useEffect(() => {
    setTimeout(() => {
      let slide = document.getElementById(currentSlide.toString())
      slide?.scrollIntoView({ behavior: "smooth" })
    }, 10)
  }, [currentSlide])

  // slides
  const slides = [
    {
      label: "Configuration",
      component: (
        <Stack spacing={2}>
          <Typography variant="h6" className="card-title">
            Configuration
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Start date"
            value={startsAt.slice(0, startsAt.indexOf("T"))}
            onChange={(e) => {
              setStartsAt(new Date(e.target.value).toISOString())

              // check for errors
              if (new Date(e.target.value) > new Date(endsAt)) {
                setStartsAtError(true)
              } else {
                setStartsAtError(false)
              }
            }}
            error={startsAtError}
            helperText={
              startsAtError ? "Start date cannot be after end date" : ""
            }
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="End date"
            value={endsAt.slice(0, endsAt.indexOf("T"))}
            onChange={(e) => {
              setEndsAt(
                new Date(e.target.value + "T21:59:59.000Z").toISOString()
              )

              // check for errors
              if (new Date(e.target.value) < new Date(startsAt)) {
                setEndsAtError(true)
              } else {
                setEndsAtError(false)
              }
            }}
            error={endsAtError}
            helperText={
              endsAtError ? "End date cannot be before start date" : ""
            }
          />
          {/* <TextField
            size="small"
            type="number"
            label="Points"
            value={points ?? ""}
            onChange={(e) => {
              setPoints(e.target.value ? parseInt(e.target.value) : null)

              // check for errors
              if (e.target.value && parseInt(e.target.value) === 0) {
                setPointsError(true)
              } else {
                setPointsError(false)
              }
            }}
            error={pointsError}
            helperText={pointsError ? "Invalid value" : ""}
          /> */}
        </Stack>
      ),
    },
    ...(!preSelectedTeam
      ? [
          {
            label: "Team",
            component: (
              <Stack spacing={2}>
                <Typography variant="h6" className="card-title">
                  Team
                </Typography>
                <TextField
                  size="small"
                  label="Team"
                  InputProps={{
                    readOnly: true,
                    endAdornment:
                      team.id === "team_default" ? (
                        <Button
                          sx={{ paddingInline: 3 }}
                          onClick={() => {
                            setChangeTeamDialogOpen(true)
                          }}
                        >
                          Change
                        </Button>
                      ) : (
                        <Stack direction="row">
                          <Button
                            sx={{ paddingInline: 3 }}
                            onClick={() => {
                              setTeam({
                                id: "team_default",
                                name: "Global (App)",
                              })
                            }}
                          >
                            Reset
                          </Button>
                          <Button
                            sx={{ paddingInline: 3 }}
                            onClick={() => {
                              setChangeTeamDialogOpen(true)
                            }}
                          >
                            Change
                          </Button>
                        </Stack>
                      ),
                  }}
                  value={team.name}
                />
              </Stack>
            ),
          },
        ]
      : []),
    {
      label: "Language",
      component: (
        <Stack spacing={2}>
          <Typography variant="h6" className="card-title">
            Default language
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="language-select">Default language</InputLabel>
            <Select
              labelId="language-select"
              label="Default language"
              value={defaultTranslation}
              onChange={(e) => {
                setDefaultTranslation(e.target.value)
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      ),
    },
    {
      label: "Details",
      component: (
        <Stack spacing={2}>
          <Typography variant="h6" className="card-title">
            Details in{" "}
            {defaultTranslation.length
              ? languagesForEpisodeTabs.find(
                  (item) => item.value === defaultTranslation
                ).label
              : ""}
          </Typography>
          <TextField
            size="small"
            label="Title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value)
            }}
            onBlur={() => {
              if (title) {
                setTitle((current) => current.trim())
              }
            }}
            helperText={
              title && title.length < constraints.news.title.min
                ? `Minimum length is ${constraints.news.title.min} characters`
                : title.length > constraints.news.title.max
                ? `Maximum length is ${constraints.news.title.max} characters`
                : ""
            }
            error={
              title &&
              (title.length < constraints.news.title.min ||
                title.length > constraints.news.title.max)
                ? true
                : false
            }
          />
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Tagline (optional)"
              value={tagline ?? ""}
              onChange={(e) => {
                if (e.target.value.length) {
                  setTagline(e.target.value)
                } else {
                  setTagline(null)
                }
              }}
              onBlur={() => {
                if (tagline) {
                  setTagline((current) => current.trim())
                }
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Redirect URL (optional)"
              value={redirectUrl ?? ""}
              onChange={(e) => {
                if (e.target.value.length) {
                  setRedirectUrl(e.target.value)
                } else {
                  setRedirectUrl(null)
                }
              }}
              error={redirectUrl && !isValidURL(redirectUrl)}
              helperText={
                redirectUrl && !isValidURL(redirectUrl) ? "Invalid URL" : ""
              }
              onBlur={() => {
                if (redirectUrl) {
                  setRedirectUrl((current) => current.trim())
                }
              }}
            />
          </Stack>
          <ImagePicker
            title="Image"
            filters={{ category: "thumbnail" }}
            disableCategoryFilter
            pickedImage={image}
            setPickedImage={(selectedImage: {
              url: string
              id: string
              description?: string
              type?: MediaSize
            }) => {
              setImage(selectedImage.url)
            }}
          />
        </Stack>
      ),
    },
    {
      label: "Body",
      component: (
        <Stack spacing={2}>
          <Typography variant="h6" className="card-title">
            Body in{" "}
            {defaultTranslation.length
              ? languagesForEpisodeTabs.find(
                  (item) => item.value === defaultTranslation
                ).label
              : ""}
          </Typography>
          {!redirectUrl ? (
            <div
              style={{
                height: 260,
                maxHeight: 260,
                overflowY: "scroll",
              }}
            >
              <BlockEditor
                body={body}
                setBody={(
                  body: (EmbedSlice | ImageSlice | QuoteSlice | TextSlice)[]
                ) => {
                  setBody([...body])
                }}
              />
            </div>
          ) : (
            <div>You cannot add both redirect URL and body</div>
          )}
        </Stack>
      ),
    },
  ]

  // reset dialog on close
  useEffect(() => {
    if (!dialogOpen) {
      setTimeout(() => {
        setStartsAt(
          new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate()
          ).toISOString()
        )
        setStartsAtError(false)
        setEndsAt(
          addDays(
            new Date(
              new Date().getFullYear(),
              new Date().getMonth(),
              new Date().getDate(),
              23,
              59,
              59
            ),
            1
          ).toISOString()
        )
        setEndsAtError(false)
        setDefaultTranslation("")
        setTitle("")
        setTagline(null)
        setRedirectUrl(null)
        setImage("")
        setCurrentSlide(0)
      }, 100)
    }
  }, [dialogOpen])

  // discard dialog
  const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false)

  const handleDiscardDialogCancel = () => {
    setDiscardDialogOpen(false)
  }

  const handleDiscardDialogClose = (e: any) => {
    setDiscardDialogOpen(false)
    handleDialogChange(e)
  }

  // should show discard dialog
  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false)

  useEffect(() => {
    if (startsAt.length || endsAt.length) {
      setShowDiscardDialog(true)
    } else {
      setShowDiscardDialog(false)
    }
  }, [startsAt, endsAt])

  // create news
  const createNewNews = async () => {
    setLoading(true)

    const input: {
      startsAt: string
      endsAt: string
      teamId: string
    } = {
      startsAt: startsAt,
      endsAt: endsAt,
      teamId: team.id,
    }

    const result = await createNews(input)

    if (result !== false) {
      const newNews = result as News

      let bodyToSet = []
      if (!redirectUrl) {
        const newBody = []
        body.forEach((bodyItem: any) => {
          const { sliceType, ...rest } = bodyItem
          newBody.push({
            [lowercaseFirstCharacter(bodyItem.sliceType)]: {
              ...rest,
            },
          })
        })
        bodyToSet = newBody
      }

      const documentInput: {
        parentId: string
        type: string
        newsDocumentItems: {
          body: object[]
          default: boolean
          image: string
          lang: string
          title: string
          redirectUrl?: string
          tagline?: string
        }[]
      } = {
        parentId: newNews.id,
        type: "News",
        newsDocumentItems: [
          {
            body: bodyToSet,
            default: true,
            image: image,
            lang: defaultTranslation,
            title: title,
            redirectUrl: redirectUrl,
            tagline: tagline,
          },
        ],
      }

      const secondResult = await createNewsDocument(documentInput)

      if (typeof secondResult !== "boolean") {
        setDialogOpen(false)

        const newNewsDocument = secondResult

        if (!preSelectedTeam) {
          setAnimation(false)
          setTimeout(() => {
            navigate(`/news/${newNews.id}`)
          }, 250)
        } else {
          newNews.document = newNewsDocument
          currentTeamNewsList.push(newNews)
          setCurrentTeamNewsList([...currentTeamNewsList])
        }
      }
    } else {
      setLoading(false)
    }
  }

  return (
    <Dialog
      fullScreen
      open={dialogOpen}
      onClose={
        showDiscardDialog
          ? () => {
              setDiscardDialogOpen(true)
            }
          : handleDialogChange
      }
      TransitionComponent={DialogTransition}
    >
      <DialogTopBar
        handleDialogChange={
          showDiscardDialog
            ? () => {
                setDiscardDialogOpen(true)
              }
            : handleDialogChange
        }
        topBarText={topBarText}
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          style={{
            width: "70%",
            minWidth: 345,
            maxWidth: 850,
            height: 500,
            position: "relative",
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            style={{ paddingTop: 16 }}
          >
            {topBarText}
          </Typography>
          <Stepper
            activeStep={currentSlide}
            style={{
              paddingTop: 24,
              paddingLeft: 8,
              paddingRight: 8,
              paddingBottom: 8,
              maxWidth: "100%",
              overflow: "overlay",
            }}
          >
            {slides.map((slide) => {
              return (
                <Step key={slide.label}>
                  <StepLabel>{slide.label}</StepLabel>
                </Step>
              )
            })}
          </Stepper>
          <div
            className="horizontal-scroll"
            style={{
              minWidth: 345,
              maxWidth: 850,
              height: "auto",
              display: "flex",
              flexDirection: "row",
              overflow: "hidden",
              scrollSnapType: "x mandatory",
            }}
          >
            {slides.map((slide, index) => (
              <div
                key={index}
                id={index.toString()}
                style={{ minWidth: "100%", scrollSnapAlign: "center" }}
              >
                <CardContent>{slide.component}</CardContent>
              </div>
            ))}
          </div>
          <CardActions
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              padding: 16,
              width: "100%",
            }}
          >
            <div style={{ width: "50%" }}>
              <Button
                variant="outlined"
                onMouseDown={(e: any) => {
                  if (!slides[currentSlide - 1]) {
                    if (showDiscardDialog) {
                      setDiscardDialogOpen(true)
                    } else {
                      handleDialogChange(e)
                    }
                  } else {
                    setCurrentSlide(currentSlide - 1)
                  }
                }}
              >
                {!slides[currentSlide - 1] ? "Close" : "Previous"}
              </Button>
            </div>
            <div
              style={{
                width: "50%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {preSelectedTeam ? (
                <LoadingButton
                  variant="contained"
                  disabled={
                    (currentSlide === 0 && (startsAtError || endsAtError)) ||
                    (currentSlide === 1 && !defaultTranslation.length) ||
                    (currentSlide === 2 && !title.length) ||
                    (currentSlide === 2 &&
                      title.length < constraints.news.title.min) ||
                    (currentSlide === 2 &&
                      title.length > constraints.news.title.max) ||
                    (currentSlide === 2 && !image.length) ||
                    (currentSlide === 2 &&
                      redirectUrl &&
                      !isValidURL(redirectUrl)) ||
                    (currentSlide === 3 && bodyError)
                  }
                  onMouseDown={
                    !slides[currentSlide + 1]
                      ? createNewNews
                      : () => {
                          setCurrentSlide(currentSlide + 1)
                        }
                  }
                >
                  {!slides[currentSlide + 1] ? "Save" : "Next"}
                </LoadingButton>
              ) : (
                <LoadingButton
                  variant="contained"
                  disabled={
                    (currentSlide === 0 && (startsAtError || endsAtError)) ||
                    (currentSlide === 2 && !defaultTranslation.length) ||
                    (currentSlide === 3 && !title.length) ||
                    (currentSlide === 3 &&
                      title.length < constraints.news.title.min) ||
                    (currentSlide === 3 &&
                      title.length > constraints.news.title.max) ||
                    (currentSlide === 3 && !image.length) ||
                    (currentSlide === 3 &&
                      redirectUrl &&
                      !isValidURL(redirectUrl)) ||
                    (currentSlide === 4 && bodyError)
                  }
                  onMouseDown={
                    !slides[currentSlide + 1]
                      ? createNewNews
                      : () => {
                          setCurrentSlide(currentSlide + 1)
                        }
                  }
                >
                  {!slides[currentSlide + 1] ? "Save" : "Next"}
                </LoadingButton>
              )}
            </div>
          </CardActions>
        </Paper>
      </div>
      <Dialog
        open={discardDialogOpen}
        onClose={handleDiscardDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to close this window?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All entered data will be lost
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDialogCancel}>Cancel</Button>
          <Button onClick={handleDiscardDialogClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingBackdrop open={loading} />
      {/* change team dialog */}
      <Dialog
        fullScreen
        open={changeTeamDialogOpen}
        onClose={() => {
          setChangeTeamDialogOpen(false)
          setSelectedTeam(null)
          resetTeamsFilters()
        }}
        TransitionComponent={DialogTransition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                setChangeTeamDialogOpen(false)
                setSelectedTeam(null)
                resetTeamsFilters()
              }}
              aria-label="close"
            >
              <CloseRounded />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Select Team
            </Typography>
            <Button
              autoFocus
              color="inherit"
              onClick={() => {
                setTeam({ id: selectedTeam.id, name: selectedTeam.name })

                let slide = document.getElementById(currentSlide.toString())
                slide?.scrollIntoView({ behavior: "auto" })

                setChangeTeamDialogOpen(false)
                setSelectedTeam(null)
                resetTeamsFilters()
              }}
              disabled={!selectedTeam}
            >
              Select
            </Button>
          </Toolbar>
        </AppBar>
        <Paper style={{ backgroundColor: "#f5f5f5" }}>
          <TeamsList
            forDialog
            selectedTeam={selectedTeam}
            setSelectedTeam={setSelectedTeam}
          />
        </Paper>
      </Dialog>
    </Dialog>
  )
}

export default CreateNewsDialog
